import { ColDef } from 'ag-grid-enterprise';
import { alignCenter, alignLeft, comparator, component, equals, mergeClasses } from './utils';
import { formatPrettyNumber } from '@/modules/common/components/pretty-number';
import { h } from 'vue';
import RateOutput from '@/modules/common/components/format-rate/RateOutput.vue';
import { DepthOfBookItem } from '@/modules/marketplace/models';
import { isEqual } from 'lodash';
import Decimal from 'decimal.js';
import { orderTypeLabel } from '@/modules/marketplace/helpers/marketplace';
import { RateType } from '@/connect';

export function borrowOrderType(): ColDef<DepthOfBookItem> {
  return {
    field: 'borrow.orderType',
    colId: 'borrow.orderType',
    headerName: '',
    valueFormatter: (params) => (!params.value ? '' : orderTypeLabel(params!.value)),
    resizable: false,
    minWidth: 60,
    width: 60,
    sortable: false,
    ...alignCenter(),
  };
}

export function borrowCounterparty(): ColDef<DepthOfBookItem> {
  return {
    field: 'borrow.counterparty',
    colId: 'borrow.counterparty',
    headerName: 'Counterparty',
    enableCellChangeFlash: true,
    equals: (a, b) => isEqual(a, b),
    valueFormatter: (params) => (!params.value ? '–' : params.value.companyName),
    ...alignLeft(),
  };
}

export function borrowQuantity(): ColDef<DepthOfBookItem> {
  return {
    field: 'borrow.quantity',
    colId: 'borrow.quantity',
    headerName: 'Quantity',
    enableCellChangeFlash: true,
    valueFormatter: (params) => (!params.value ? '–' : formatPrettyNumber(params.value)),
    comparator: comparator.number,
    ...alignLeft(),
  };
}

const rateAdapter = component<{
  rate: number | string | Decimal | null | undefined;
}>(
  (props) => () =>
    props.rate === null || props.rate === undefined ? h('span', '–') : h(RateOutput, { props })
);

export function borrowRate(config: { rateType: RateType }): ColDef<DepthOfBookItem> {
  return {
    field: config.rateType === RateType.FEE ? 'borrow.fee' : 'borrow.rebate',
    colId: 'borrow.rate',
    headerName: 'Bid',
    enableCellChangeFlash: true,
    equals: equals.decimal,
    cellDataType: 'text',
    cellRendererSelector: (params) =>
      rateAdapter({
        rate: params.value,
      }),
    comparator: comparator.decimal,
    ...mergeClasses([alignLeft(), { cellClass: 'highlighted-border-right' }]),
  };
}

export function lendRate(config: { rateType: RateType }): ColDef<DepthOfBookItem> {
  return {
    field: config.rateType === RateType.FEE ? 'lend.fee' : 'lend.rebate',
    colId: 'lend.rate',
    headerName: 'Ask',
    enableCellChangeFlash: true,
    equals: equals.decimal,
    cellDataType: 'text',
    cellRendererSelector: (params) =>
      rateAdapter({
        rate: params.value,
      }),
    comparator: comparator.decimal,
    ...alignLeft(),
  };
}

export function lendQuantity(): ColDef<DepthOfBookItem> {
  return {
    field: 'lend.quantity',
    colId: 'lend.quantity',
    headerName: 'Quantity',
    enableCellChangeFlash: true,
    equals: (a, b) => isEqual(a, b),
    valueFormatter: (params) => (!params.value ? '–' : formatPrettyNumber(params.value)),
    comparator: comparator.number,
    ...alignLeft(),
  };
}

export function lendCounterparty(): ColDef<DepthOfBookItem> {
  return {
    field: 'lend.counterparty',
    colId: 'lend.counterparty',
    headerName: 'Counterparty',
    enableCellChangeFlash: true,
    equals: (a, b) => isEqual(a, b),
    valueFormatter: (params) => (!params.value ? '–' : params.value.companyName),
    ...alignLeft(),
  };
}

export function lendOrderType(): ColDef<DepthOfBookItem> {
  return {
    field: 'lend.orderType',
    colId: 'lend.orderType',
    headerName: '',
    valueFormatter: (params) => (!params.value ? '' : orderTypeLabel(params!.value)),
    resizable: false,
    minWidth: 60,
    width: 60,
    sortable: false,
    ...alignCenter(),
  };
}
